import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, Package, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../services/api";

/**
 * Razorpay redirect-flow success handler.
 * Razorpay redirects here with: razorpay_payment_id, razorpay_order_id, razorpay_signature
 * (or with razorpay_payment_link_id, etc — we only need the 3 above for Order checkout).
 *
 * Flow:
 *   1. Verify signature server-side via /payments/confirm-online → creates the order.
 *   2. If verify returns 200 → show success + tracking id.
 *   3. If verify times out / fails → poll every 2s up to 20s; the webhook is the fallback.
 *   4. After 20s with no success → show "Payment received, confirming…" message.
 */
export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState("verifying"); // verifying | success | confirming | error
  const [trackingId, setTrackingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const pollAttempts = useRef(0);

  const rz_order_id =
    params.get("razorpay_order_id") ||
    (typeof window !== "undefined" && sessionStorage.getItem("pickngo_pending_rz_order")) || "";
  const rz_payment_id = params.get("razorpay_payment_id") || "";
  const rz_signature = params.get("razorpay_signature") || "";

  // Razorpay redirect-mode failures land here too (not on /payment-failed
  // directly). Detect the error[code] / error[description] query params and
  // forward to the proper failure UI.
  const rz_error_code = params.get("error[code]") || params.get("error_code") || "";
  const rz_error_desc = params.get("error[description]") || params.get("error_description") || "";

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    // Razorpay failure: redirect to /payment-failed with the same params so
    // the user sees a clean error UI instead of the misleading
    // "no payment details" branch below.
    if (rz_error_code) {
      const code = encodeURIComponent(rz_error_code);
      const desc = encodeURIComponent(rz_error_desc || "Payment was not completed");
      const oid = encodeURIComponent(rz_order_id || "");
      navigate(`/payment-failed?error_code=${code}&error_description=${desc}&order_id=${oid}`, { replace: true });
      sessionStorage.removeItem("pickngo_pending_rz_order");
      return () => { cancelled = true; };
    }

    const checkPendingViaApi = async () => {
      // Webhook fallback path: hit a tiny GET to see if the intent has been
      // consumed (which means the webhook beat us and the order is live).
      try {
        const { data } = await api.get(`/payments/intent-status/${rz_order_id}`);
        if (data?.status === "consumed" && data?.tracking_id) {
          if (!cancelled) {
            setTrackingId(data.tracking_id);
            setState("success");
            sessionStorage.removeItem("pickngo_pending_rz_order");
          }
          return true;
        }
      } catch (pollErr) {
        // intent-status endpoint may legitimately return 404 while the
        // webhook hasn't run yet, or the route may not exist at all on
        // older deployments. Both are non-fatal — log at debug level so
        // the silent failure is still discoverable in the browser console.
        console.debug("intent-status poll failed (will retry):", pollErr?.message || pollErr);
      }
      return false;
    };

    const verifyNow = async () => {
      if (!rz_order_id || !rz_payment_id || !rz_signature) {
        // No payment params — likely landed here directly. Just bounce to orders.
        setState("error");
        setErrorMsg("No payment details on this page. Redirecting to your orders…");
        setTimeout(() => navigate("/dashboard/orders"), 3500);
        return;
      }

      try {
        const { data } = await api.post("/payments/confirm-online", {
          razorpay_order_id: rz_order_id,
          razorpay_payment_id: rz_payment_id,
          razorpay_signature: rz_signature,
        });
        if (cancelled) return;
        setTrackingId(data.tracking_id);
        setState("success");
        sessionStorage.removeItem("pickngo_pending_rz_order");
      } catch (err) {
        // Verify failed in this browser. Webhook may still complete it server-side.
        // Poll the intent-status endpoint up to 10× / 20s, then show "confirming…"
        const detail = err?.response?.data?.detail || "Verification failed";
        const status = err?.response?.status;
        if (status === 400 || status === 403) {
          // Hard-fail: bad signature or wrong user.
          setState("error");
          setErrorMsg(detail);
          return;
        }
        setState("confirming");
        const poll = async () => {
          if (cancelled) return;
          pollAttempts.current += 1;
          const consumed = await checkPendingViaApi();
          if (consumed) return;
          if (pollAttempts.current < 10) {
            pollTimer = setTimeout(poll, 2000);
          } else {
            // Give up the live poll — show friendly "we'll process shortly" UI.
            // The webhook will still create the order in the background.
            setState("confirming_long");
          }
        };
        pollTimer = setTimeout(poll, 2000);
      }
    };

    verifyNow();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── UI states ───────────────────────────────────────────────────────
  if (state === "verifying") {
    return (
      <Shell title="Verifying payment…" testid="payment-verifying">
        <Loader2 className="h-10 w-10 animate-spin text-[#FF6B00]" />
        <p className="text-sm text-slate-500 mt-4">Confirming your payment with Razorpay…</p>
      </Shell>
    );
  }

  if (state === "success") {
    return (
      <Shell title="Payment Successful" tone="success" testid="payment-success">
        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-5">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
        <p className="text-sm text-slate-500 mb-3">
          Your payment is confirmed. Order ID:{" "}
          <span className="font-mono font-bold text-slate-900" data-testid="success-tracking-id">{trackingId}</span>
        </p>
        <div className="flex gap-3 w-full mt-4">
          <Button onClick={() => navigate("/dashboard/orders")} variant="outline" className="flex-1 rounded-xl h-11 border-slate-200" data-testid="payment-go-orders">
            <Package className="h-4 w-4 mr-1.5" /> My Orders
          </Button>
          <Button onClick={() => navigate("/dashboard/tracking")} className="flex-1 bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-11" data-testid="payment-go-tracking">
            Track Order <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </Shell>
    );
  }

  if (state === "confirming") {
    return (
      <Shell title="Confirming your booking…" tone="info" testid="payment-confirming">
        <Loader2 className="h-10 w-10 animate-spin text-[#FF6B00]" />
        <p className="text-sm text-slate-500 mt-4">Payment received. Confirming with our system…</p>
        <p className="text-xs text-slate-400 mt-1">This usually takes a few seconds.</p>
      </Shell>
    );
  }

  if (state === "confirming_long") {
    return (
      <Shell title="Payment received" tone="info" testid="payment-confirming-long">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        <p className="text-sm text-slate-700 font-medium mt-4 text-center">
          Payment received, confirming your booking.
        </p>
        <p className="text-xs text-slate-500 mt-1 text-center">
          Your order will appear in <span className="font-semibold">My Orders</span> shortly.
        </p>
        <Button onClick={() => navigate("/dashboard/orders")} className="mt-5 bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 px-6">
          Go to My Orders <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </Shell>
    );
  }

  // error
  return (
    <Shell title="Couldn't verify payment" tone="error" testid="payment-error">
      <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mb-4">
        <AlertCircle className="h-7 w-7 text-red-600" />
      </div>
      <p className="text-sm text-slate-600 text-center max-w-xs">{errorMsg || "If your payment went through, your order will still appear shortly via our payment notifier."}</p>
      <Button onClick={() => navigate("/dashboard/orders")} className="mt-5 bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11">
        Back to My Orders
      </Button>
    </Shell>
  );
}

function Shell({ title, children, tone = "default", testid }) {
  const tones = {
    default: "border-slate-200",
    success: "border-green-200",
    info:    "border-orange-200",
    error:   "border-red-200",
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-4">
      <div
        className={`w-full max-w-md bg-white rounded-2xl border ${tones[tone] || tones.default} shadow-lg p-8 flex flex-col items-center`}
        data-testid={testid || "payment-shell"}
      >
        <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900 mb-2 text-center">{title}</h2>
        {children}
      </div>
    </div>
  );
}
